// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app/root.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$(),
  _s2 = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app/root.tsx");
}
// REMIX HMR END

import { json, redirect } from '@remix-run/cloudflare';
import { Links, LiveReload, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from '@remix-run/react';
import { parse } from 'cookie';
import { useRef } from 'react';
import { useFullscreen, useToggle } from 'react-use';
import { QueryClient, QueryClientProvider } from 'react-query';
import tailwind from '~/styles/tailwind.css';
import { authenticator } from './services/auth.server';
import { elementNotContainedByClickTarget } from './utils/elementNotContainedByClickTarget';
import getUsername from './utils/getUsername.server';
import { cn } from './utils/style';
function addOneDay(date) {
  const result = new Date(date);
  result.setTime(result.getTime() + 24 * 60 * 60 * 1000);
  return result;
}
export const loader = async ({
  request,
  context,
  params: _params
}) => {
  const url = new URL(request.url);

  // Skip auth check for auth-related routes
  const isAuthRoute = url.pathname.startsWith('/auth/') || url.pathname === '/login' || url.pathname === '/set-username';

  // All API routes should be public - both dot-notation and nested routes
  const isApiRoute = url.pathname.startsWith('/api/') || url.pathname.match(/^\/api\.[a-zA-Z0-9]+$/) !== null;

  // Publicly accessible pages, including the main homepage
  const isPublicPage = ['/', '/privacy', '/tos', '/support'].includes(url.pathname);

  // Public assets
  const isPublicAsset = url.pathname.startsWith('/build/') || url.pathname.startsWith('/static/') || url.pathname.match(/\.(js|css|png|jpg|ico|svg)$/i);

  // Skip authentication checks for auth routes, API routes, public assets, and public pages
  if (isAuthRoute || isApiRoute || isPublicAsset || isPublicPage) {
    return json({
      userDirectoryUrl: context.env.USER_DIRECTORY_URL,
      currentUser: null,
      env: {
        GOOGLE_CLIENT_ID: context.env.GOOGLE_CLIENT_ID
      }
    });
  }

  // Get the authenticated user if available
  let username = null;
  let currentUser = null;
  try {
    // Get username using existing methods - don't need to authenticate for Google here
    username = await getUsername(request, context);
  } catch (error) {
    console.error('Error getting username:', error);
  }

  // Check authentication only if needed and prevent redirect loops
  if (!username) {
    // Prevent redirect loops by checking if we're already redirecting to login
    const redirectUrl = new URL(url);
    redirectUrl.pathname = '/login';

    // IMPORTANT: Never redirect API routes or public paths
    const skipRedirect = isApiRoute || isAuthRoute || isPublicAsset || url.pathname === '/';

    // Make sure we're not already on the login page (to prevent loops)
    if (url.pathname !== '/login' && !skipRedirect) {
      redirectUrl.searchParams.set('return-url', request.url);
      throw redirect(redirectUrl.toString());
    }
  }

  // Safely try to get Google profile info for the current user
  try {
    if (username) {
      // Only try to load the Google profile if we know the user is logged in
      currentUser = await authenticator.isAuthenticated(request);
      console.log('Found Google profile:', currentUser?.displayName, currentUser?.photos?.[0]?.value);
    }
  } catch (error) {
    // Just log the error but continue - this is optional enhancement
    console.error('Could not load Google profile:', error);
  }
  const defaultResponse = json({
    userDirectoryUrl: context.env.USER_DIRECTORY_URL,
    currentUser,
    env: {
      GOOGLE_CLIENT_ID: context.env.GOOGLE_CLIENT_ID
    }
  });

  // we only care about verifying token freshness if request was a user
  // initiated document request.
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Sec-Fetch-User
  const secFetchUser = request.headers.get('Sec-Fetch-User');
  if (secFetchUser !== '?1') return defaultResponse;
  const cookiesHeader = request.headers.get('Cookie');
  if (!cookiesHeader) return defaultResponse;
  const {
    CF_Authorization
  } = parse(cookiesHeader);
  if (!CF_Authorization) return defaultResponse;
  const [, payload] = CF_Authorization.split('.');
  const data = JSON.parse(atob(payload));
  const expires = new Date(data.exp * 1000);
  const now = new Date();
  if (addOneDay(now) > expires) {
    const headers = new Headers();
    ['CF_Authorization', 'CF_AppSession'].forEach(cookieName => headers.append('Set-Cookie', `${cookieName}=; Expires=${new Date(0).toUTCString()}; Path=/;`));
    throw redirect(request.url, {
      headers
    });
  }
  return defaultResponse;
};
export const meta = () => [{
  title: 'Clamo'
}];
export const links = () => [{
  rel: 'stylesheet',
  href: tailwind
}, {
  rel: 'apple-touch-icon',
  sizes: '180x180',
  href: '/apple-touch-icon.png?v=shell-emoji'
}, {
  rel: 'icon',
  type: 'image/png',
  sizes: '32x32',
  href: '/favicon-32x32.png?v=shell-emoji'
}, {
  rel: 'icon',
  type: 'image/png',
  sizes: '16x16',
  href: '/favicon-16x16.png?v=shell-emoji'
}, {
  rel: 'manifest',
  href: '/site.webmanifest',
  crossOrigin: 'use-credentials'
}, {
  rel: 'mask-icon',
  href: '/safari-pinned-tab.svg?v=shell-emoji',
  color: '#00a5e3'
}, {
  rel: 'shortcut icon',
  href: '/favicon.ico?v=shell'
}];
const Document = ({
  children
}) => {
  _s();
  const fullscreenRef = useRef(null);
  const [fullscreenEnabled, toggleFullscreen] = useToggle(false);
  useFullscreen(fullscreenRef, fullscreenEnabled, {
    onClose: () => toggleFullscreen(false)
  });
  return (
    // some extensions add data attributes to the html
    // element that React complains about.
    <html className="h-full" lang="en" suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta name="apple-mobile-web-app-title" content="Clamo" />
				<meta name="application-name" content="Clamo" />
				<meta name="msapplication-TileColor" content="#ffffff" />
				<meta name="theme-color" content="#ffffff" />
				<Meta />
				<Links />
			</head>
			<body className={cn('h-full', 'skype-bg-primary', 'skype-text-primary')} ref={fullscreenRef} onDoubleClick={e => {
        if (e.target instanceof HTMLElement && !elementNotContainedByClickTarget(e.target)) toggleFullscreen();
      }}>

				{children}
				<ScrollRestoration />
				<div className="hidden" suppressHydrationWarning>
					{/* Replaced in entry.server.ts */}
					__CLIENT_ENV__
				</div>
				<Scripts />
				<LiveReload />
			</body>
		</html>
  );
};
_s(Document, "EnJpkk45ATvxzMOYfjCE43rzFUc=", false, function () {
  return [useToggle, useFullscreen];
});
_c = Document;
export const ErrorBoundary = () => {
  return <Document>
			<div className="grid h-full place-items-center">
				<p>
					It looks like there was an error, but don't worry it has been
					reported. Sorry about that!
				</p>
			</div>
		</Document>;
};
_c2 = ErrorBoundary;
export default function App() {
  _s2();
  const {
    userDirectoryUrl,
    currentUser
  } = useLoaderData();
  const [isFullscreen, toggleFullscreen] = useToggle(false);
  const fullscreenRef = useRef(null);
  useFullscreen(fullscreenRef, isFullscreen, {
    onClose: () => toggleFullscreen(false)
  });
  const queryClient = new QueryClient();

  // We're now loading the Google Identity Services script directly in the set-username.tsx component
  // to avoid conflicts and ensure it's only loaded when needed

  return <html lang="en" className="h-full">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="h-full">
				<div ref={fullscreenRef} className="h-full">
					<QueryClientProvider client={queryClient}>
						<Outlet context={{
            userDirectoryUrl,
            currentUser
          }} />
					</QueryClientProvider>
				</div>
				<ScrollRestoration />
				<div className="hidden" suppressHydrationWarning>
					{/* Replaced in entry.server.ts */}
					__CLIENT_ENV__
				</div>
				<Scripts />
				<LiveReload />
			</body>
		</html>;
}
_s2(App, "SyHMvLm7g9tXeBe9m/ppWiXCNtQ=", false, function () {
  return [useLoaderData, useToggle, useFullscreen];
});
_c3 = App;
var _c, _c2, _c3;
$RefreshReg$(_c, "Document");
$RefreshReg$(_c2, "ErrorBoundary");
$RefreshReg$(_c3, "App");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;